'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

// All possible nav items tagged with which roles can see them
// roles: 'client' | 'team_member' | 'team' | 'provider' | 'manager' | 'admin' | 'guest'
// undefined roles = visible to all authenticated users

interface NavItem {
  label: string;
  href: string;
  icon: string;
  roles?: string[]; // if undefined → visible to all logged-in users
}

const ALL_NAV_ITEMS: NavItem[] = [
  {
    label: 'Request a Quote',
    href: '/',
    icon: 'DocumentTextIcon',
    roles: ['client', 'guest'],
  },
  {
    label: 'My Jobs',
    href: '/client-job-portal',
    icon: 'BriefcaseIcon',
    roles: ['client'],
  },
  {
    label: 'My Work',
    href: '/dashboard/team',
    icon: 'ClipboardDocumentListIcon',
    roles: ['team_member', 'team'],
  },
  {
    label: 'My Jobs',
    href: '/dashboard/provider',
    icon: 'WrenchScrewdriverIcon',
    roles: ['provider'],
  },
  {
    label: 'Manager',
    href: '/manager',
    icon: 'ClipboardDocumentCheckIcon',
    roles: ['manager', 'admin'],
  },
  {
    label: 'Internal Notes',
    href: '/internal-notes',
    icon: 'ClipboardDocumentListIcon',
    roles: ['manager', 'admin', 'team_member', 'team'],
  },
  {
    label: 'Analytics',
    href: '/analytics',
    icon: 'ChartBarIcon',
    roles: ['manager', 'admin'],
  },
  {
    label: 'Accounts',
    href: '/account-management',
    icon: 'UserGroupIcon',
    roles: ['manager', 'admin'],
  },
];

interface DashboardItem {
  label: string;
  href: string;
  icon: string;
  roles: string[];
}

const ALL_DASHBOARD_ITEMS: DashboardItem[] = [
  {
    label: 'Client Portal',
    href: '/dashboard/client',
    icon: 'UserIcon',
    roles: ['client', 'manager', 'admin'],
  },
  {
    label: 'Team Dashboard',
    href: '/dashboard/team',
    icon: 'UserGroupIcon',
    roles: ['team_member', 'team', 'manager', 'admin'],
  },
  {
    label: 'Provider Portal',
    href: '/dashboard/provider',
    icon: 'BuildingOfficeIcon',
    roles: ['provider', 'manager', 'admin'],
  },
  {
    label: 'Management',
    href: '/dashboard/management',
    icon: 'ChartBarSquareIcon',
    roles: ['manager', 'admin'],
  },
];

interface TopbarProps {
  activeRoute?: string;
}

export default function Topbar({ activeRoute }: TopbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [dashOpen, setDashOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { user, signOut, getUserRole } = useAuth();
  const router = useRouter();

  const role = getUserRole();

  // Filter nav items by role
  const navItems = ALL_NAV_ITEMS.filter((item) => {
    if (!item.roles) return true; // visible to all
    return item.roles.includes(role);
  });

  // Filter dashboard items by role
  const dashboardItems = ALL_DASHBOARD_ITEMS.filter((item) =>
    item.roles.includes(role)
  );

  // Only show Dashboards dropdown if there are items to show
  const showDashboardDropdown = dashboardItems.length > 0;

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setLoggingOut(true);
    setUserMenuOpen(false);
    try {
      await signOut();
      router.push('/launch');
    } catch {
      setLoggingOut(false);
    }
  };

  // Derive display name from user
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || '';
  const initials = displayName
    ? displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <header className="sticky top-0 z-50 bg-card border-b border-border shadow-sm">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 flex-shrink-0">
            <AppLogo size={36} />
            <span className="font-extrabold text-xl tracking-tight text-foreground hidden sm:block">
              First<span className="text-primary">Served</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = activeRoute === item.href;
              return (
                <Link
                  key={`nav-${item.href}-${item.label}`}
                  href={item.href}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                    isActive
                      ? 'bg-secondary text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon name={item.icon as any} size={16} variant="outline" />
                  {item.label}
                </Link>
              );
            })}

            {/* Dashboards dropdown — only shown if user has accessible dashboards */}
            {showDashboardDropdown && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setDashOpen(!dashOpen)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                    dashboardItems.some((d) => activeRoute === d.href)
                      ? 'bg-secondary text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon name="Squares2X2Icon" size={16} variant="outline" />
                  Dashboards
                  <Icon name={dashOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={13} variant="outline" />
                </button>
                {dashOpen && (
                  <div className="absolute top-full left-0 mt-1 w-52 bg-card border border-border rounded-xl shadow-lg py-1 z-50 animate-fade-in">
                    {dashboardItems.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => setDashOpen(false)}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                          activeRoute === item.href
                            ? 'text-primary bg-secondary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`}
                      >
                        <Icon name={item.icon as any} size={15} variant="outline" />
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {/* New Request button — only for clients and guests */}
            {(role === 'client' || role === 'guest') && (
              <Link
                href="/"
                className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:opacity-90 transition-all duration-150 active:scale-95"
              >
                <Icon name="PlusIcon" size={16} variant="outline" />
                New Request
              </Link>
            )}

            {/* User menu dropdown — only shown when user is signed in */}
            {user && (
              <div className="relative hidden md:block" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted transition-all duration-150"
                  title="Account menu"
                >
                  {/* Avatar circle with initials */}
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold flex-shrink-0">
                    {initials}
                  </div>
                  <div className="hidden lg:flex flex-col items-start leading-tight">
                    <span className="text-sm font-semibold text-foreground max-w-[120px] truncate">{displayName}</span>
                    <span className="text-xs text-muted-foreground capitalize">{role}</span>
                  </div>
                  <Icon name={userMenuOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={13} variant="outline" className="text-muted-foreground" />
                </button>

                {/* Dropdown */}
                {userMenuOpen && (
                  <div className="absolute top-full right-0 mt-1 w-56 bg-card border border-border rounded-xl shadow-lg py-1 z-50 animate-fade-in">
                    {/* User info header */}
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                      <span className="mt-1 inline-block text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full font-medium capitalize">{role}</span>
                    </div>
                    {/* Menu items */}
                    <Link
                      href="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                      <Icon name="UserCircleIcon" size={16} variant="outline" />
                      My Profile
                    </Link>
                    <div className="border-t border-border my-1" />
                    <button
                      type="button"
                      onClick={handleLogout}
                      disabled={loggingOut}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      {loggingOut ? (
                        <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
                      ) : (
                        <Icon name="ArrowLeftOnRectangleIcon" size={16} variant="outline" />
                      )}
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
              aria-label="Toggle menu"
            >
              <Icon name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} variant="outline" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-card animate-fade-in">
          <div className="px-4 py-3 flex flex-col gap-1">
            {/* Mobile user info */}
            {user && (
              <div className="flex items-center gap-3 px-3 py-3 mb-1 bg-muted/50 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-bold flex-shrink-0">
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>
            )}

            {navItems.map((item) => {
              const isActive = activeRoute === item.href;
              return (
                <Link
                  key={`mobile-nav-${item.href}-${item.label}`}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-secondary text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon name={item.icon as any} size={18} variant="outline" />
                  {item.label}
                </Link>
              );
            })}

            {/* Mobile dashboard links */}
            {showDashboardDropdown && (
              <div className="pt-1 border-t border-border mt-1">
                <p className="text-xs font-semibold text-muted-foreground px-3 py-1.5 uppercase tracking-wide">Dashboards</p>
                {dashboardItems.map((item) => (
                  <Link
                    key={`mobile-dash-${item.href}`}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      activeRoute === item.href
                        ? 'bg-secondary text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon name={item.icon as any} size={18} variant="outline" />
                    {item.label}
                  </Link>
                ))}
              </div>
            )}

            <div className="pt-2 border-t border-border mt-1 flex flex-col gap-2">
              {(role === 'client' || role === 'guest') && (
                <Link
                  href="/"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:opacity-90 transition-all"
                >
                  <Icon name="PlusIcon" size={16} variant="outline" />
                  New Request
                </Link>
              )}
              {user && (
                <Link
                  href="/profile"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted rounded-lg transition-all"
                >
                  <Icon name="UserCircleIcon" size={16} variant="outline" />
                  My Profile
                </Link>
              )}
              {user && (
                <button
                  onClick={() => { setMobileOpen(false); handleLogout(); }}
                  disabled={loggingOut}
                  className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50"
                >
                  {loggingOut ? (
                    <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
                  ) : (
                    <Icon name="ArrowLeftOnRectangleIcon" size={16} variant="outline" />
                  )}
                  Log Out
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}